import { CursorState } from '@editor/config/constants/cursor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setHoveredElementId, toggleSelectedElementIds } from '@editor/store/tool'
import { ThreeEvent } from '@react-three/fiber'
import { useSelector } from 'react-redux'

import { HoverContainerProps } from '../definitions'

const useHoverContainerEventHandlers = ({ id, onDown }: HoverContainerProps) => {
  // Store
  const dispatch = useAppDispatch()
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)

  const isSelectedTool = selectedTool === BlueprintEditorTools.MeasurementLabel

  /**
   * Handle mouse enter event.
   * @param ev The mouse event.
   * @returns
   */
  const handleMouseEnter = (ev: ThreeEvent<MouseEvent>) => {
    if (editingElementId) return

    ev.stopPropagation()
    dispatch(setHoveredElementId(id))
    dispatch(setCursor(CursorState.MOVE))
  }

  /**
   * Handle mouse leave event.
   * @param ev The mouse event.
   */
  const handleMouseLeave = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    dispatch(setHoveredElementId(''))
    dispatch(setCursor(isSelectedTool ? CursorState.CROSSHAIR : CursorState.DEFAULT))
  }

  /**
   * Handle mouse down event.
   * Most logic is handled in parent component. onDown is called to notify parent
   * the start of a drag operation.
   *
   * @param ev The mouse event.
   */
  const handleMouseDown = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    onDown?.()
  }

  /**
   * Handle click event.
   * Selects the element if not already selected.
   *
   * @param ev The mouse event.
   */
  const handleClick = (ev: ThreeEvent<MouseEvent>) => {
    ev.stopPropagation()
    dispatch(toggleSelectedElementIds({ ids: [id], selected: true }))
  }

  return {
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleClick,
  }
}

export default useHoverContainerEventHandlers
